/**
 * MarketingHookDetail — placeholder detail page for individual marketing hooks/campaigns.
 * Routes from /#/marketing/hooks/:slug.
 *
 * This is a v1 placeholder. Real campaign detail UI ships next sprint.
 */
import { useRoute } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";
const MI6_BG   = "#FAFAF7";

const HOOKS: Record<string, { name: string; channel: string; running: number; mqls: number; description: string }> = {
  "manufacturing-oem-pain-audit": {
    name: "Manufacturing OEM pain audit",
    channel: "LinkedIn",
    running: 12,
    mqls: 184,
    description: "Targeted audit of pain points across mid-market mfg OEMs. Lead magnet: 9-question diagnostic.",
  },
  "helix-account-audit-teaser": {
    name: "Helix-style account audit teaser",
    channel: "Email",
    running: 6,
    mqls: 92,
    description: "Drip series introducing the Helix scoring model with a public account audit calculator.",
  },
  "roundtable-replay-drip": {
    name: "Roundtable replay drip",
    channel: "Webinar followup",
    running: 18,
    mqls: 124,
    description: "Post-roundtable nurture sequence for attendees and no-shows. 5-touch over 14 days.",
  },
  "cfo-roi-calculator": {
    name: "CFO ROI calculator",
    channel: "Web",
    running: 3,
    mqls: 41,
    description: "Gated CFO-facing ROI calculator. High intent, low volume so far.",
  },
};

export default function MarketingHookDetail() {
  const [, params] = useRoute("/marketing/hooks/:slug");
  const slug = params?.slug ?? "";
  const hook = HOOKS[slug];

  return (
    <OperatorShell breadcrumbs={[{ label: "Marketing Hooks" }]} pageTitle="Hook Detail" pageMeta="Marketing hook detail">
      <div className="max-w-5xl mx-auto">
        <a
          href="/dashboards/marketing"
          className="text-sm font-semibold"
          style={{ color: MI6_RED }}
        >
          ← Back to Marketing dashboard
        </a>

        {hook ? (
          <div className="mt-6 bg-white rounded-xl border border-neutral-200 p-8" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
              {hook.channel} · running {hook.running} days
            </div>
            <h1 className="text-3xl font-extrabold mb-4" style={{ color: MI6_BLACK }}>
              {hook.name}
            </h1>
            <p className="text-neutral-600 leading-relaxed mb-8">{hook.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold mb-1">MQLs to date</div>
                <div className="text-3xl font-extrabold" style={{ color: MI6_BLACK }}>{hook.mqls}</div>
              </div>
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold mb-1">Days running</div>
                <div className="text-3xl font-extrabold" style={{ color: MI6_BLACK }}>{hook.running}</div>
              </div>
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold mb-1">Channel</div>
                <div className="text-2xl font-extrabold" style={{ color: MI6_BLACK }}>{hook.channel}</div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-neutral-200 text-sm text-neutral-500">
              Detailed campaign analytics, creative variants, and channel breakdown ship next sprint.
            </div>
          </div>
        ) : (
          <div className="mt-6 bg-white rounded-xl border border-neutral-200 p-8 text-center" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <h1 className="text-2xl font-extrabold mb-2" style={{ color: MI6_BLACK }}>
              Hook not found
            </h1>
            <p className="text-neutral-600">
              No active campaign with slug <span className="font-mono text-neutral-800">{slug}</span>.
            </p>
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
